import {Box, CircularProgress, Typography} from '@mui/material';
import * as Plot from '@observablehq/plot';
import dayjs from 'dayjs';
import {useEffect, useRef} from 'react';
import type {QuizSessionWithScore} from '@cp/model';

interface AccuracyTrendChartProps {
  sessions: QuizSessionWithScore[];
  loading: boolean;
}

export default function AccuracyTrendChart({sessions, loading}: AccuracyTrendChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading || !containerRef.current) return;

    if (!sessions || sessions.length === 0) {
      containerRef.current.innerHTML = '';
      return;
    }

    // Group sessions by week and calculate average accuracy
    const weeklyData = new Map<string, {accuracy: number; count: number; weekStart: string}>();

    sessions.forEach((session) => {
      if (session.endedAt) {
        const weekStart = dayjs(session.endedAt).startOf('week').format('YYYY-MM-DD');
        const accuracy = session.score || 0;

        const existing = weeklyData.get(weekStart);
        if (existing) {
          existing.accuracy += accuracy;
          existing.count += 1;
        } else {
          weeklyData.set(weekStart, {
            accuracy,
            count: 1,
            weekStart,
          });
        }
      }
    });

    // Convert to chart data and sort by date
    const chartData = Array.from(weeklyData.values())
      .map((data) => ({
        week: dayjs(data.weekStart).format('MMM DD'),
        fullWeek: dayjs(data.weekStart).format('MMM DD, YYYY'),
        accuracy: Math.round((data.accuracy / data.count) * 10) / 10,
        sessions: data.count,
      }))
      .sort((a, b) => dayjs(a.fullWeek).valueOf() - dayjs(b.fullWeek).valueOf())
      .slice(-8); // Show last 8 weeks

    const plotOptions: Plot.PlotOptions = {
      marks: [
        Plot.gridY({stroke: '#f0f0f0', strokeDasharray: '3,3'}),
        Plot.lineY(chartData, {
          x: 'week',
          y: 'accuracy',
          stroke: '#2e7d32',
          strokeWidth: 3,
          marker: true,
          tip: {
            format: {
              x: (d: string) => {
                const data = chartData.find((item) => item.week === d);
                return `Week of ${data?.fullWeek || d}`;
              },
              y: (d: number) => `Accuracy: ${d}%`,
            },
          },
        }),
      ],
      x: {
        label: 'Week',
        type: 'band',
      },
      y: {
        label: 'Accuracy (%)',
        domain: [0, 100],
        type: 'linear',
      },
      marginTop: 20,
      marginRight: 30,
      marginBottom: 5,
      marginLeft: 20,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    };

    const chart = Plot.plot(plotOptions);
    const container = containerRef.current;
    container.innerHTML = '';
    container.appendChild(chart);

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [sessions, loading]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Box sx={{textAlign: 'center', py: 4}}>
        <Typography variant="body1" color="text.secondary">
          No session data available yet. Complete some study sessions to see your progress!
        </Typography>
      </Box>
    );
  }

  return <Box ref={containerRef} sx={{height: 300}} />;
}
